import { ProductionBatch } from '@interfaces/search';
import { ncFileStatuses, withERP } from '@helpers';
import { InfoProps } from '@v2/context/ProductionBatch/types';

type Statistics = InfoProps['statistics'];

const MAX_ACTUAL_TOTAL_SECONDS = 60 * 60 * 100;

export const shouldHideInfoOnHover = ({
  isWithERP,
  links,
  ncFileStatus,
}: {
  links: ProductionBatch['links'];
  ncFileStatus: ProductionBatch['ncFileStatus'];
  isWithERP: boolean | null;
}) => {
  return !links?.length && (ncFileStatus === ncFileStatuses.Unknown || (ncFileStatus === ncFileStatuses.Linked && withERP(isWithERP)));
};

export const getOffsetInSeconds = (offset: number): number => Math.floor(offset / 1000);

export const getInitialActualTotal = (
  batch: ProductionBatch,
  statistics: Statistics,
  offsetInSeconds: number,
): number | undefined => {
  const base = batch.isInProgress ? statistics?.actualTotalTimeTotalInSeconds : batch?.actualTotalTimeTotalInSeconds;
  return base != null ? base + offsetInSeconds : undefined;
};

export const getInitialScheduledTime = (
  batch: ProductionBatch,
  statistics: Statistics,
): number | null | undefined => {
  return batch.isInProgress ? statistics?.scheduledTotalTimeTotalInSeconds : batch?.scheduledTotalTimeTotalInSeconds;
};

export const calculateProgress = (actualTotal: number | null | undefined, scheduledTime: number | null | undefined): number | null => {
  if (actualTotal == null || scheduledTime == null) return null;
  return actualTotal / scheduledTime;
};

export const getShouldNotRunTime = (isInProgress: boolean | undefined, actualTotal: number | undefined): boolean => {
  return !isInProgress || actualTotal == null || actualTotal >= MAX_ACTUAL_TOTAL_SECONDS;
};

export const getImageDimensions = ({ small, middle, large }: { small: boolean; middle: boolean; large: boolean }): { width: number; height: number } => {
  if (small) return { width: 108, height: 72 };
  if (middle) return { width: 156, height: 104 };
  if (large) return { width: 345, height: 230 };
  return { width: 180, height: 120 };
};

export const parseBatchNumber = (batchNumber: string | null | undefined): [string | undefined, string | undefined] => {
  if (!batchNumber) return [undefined, undefined];
  const parts = batchNumber.split('-');
  return [parts[0], parts[1]];
};

export const shouldHideLinkedInfoIfNoERP = (
  links: ProductionBatch['links'],
  ncFileStatus: ProductionBatch['ncFileStatus'],
  isWithERP: boolean | null,
): boolean => {
  return !links?.length && ncFileStatus === ncFileStatuses.Linked && !withERP(isWithERP);
};

export const shouldHideLinkedInfo = (
  links: ProductionBatch['links'],
  ncFileStatus: ProductionBatch['ncFileStatus'],
): boolean => {
  return !links?.length && (ncFileStatus === ncFileStatuses.Ignored || ncFileStatus === ncFileStatuses.Setup);
};

export const getLinkedInfoText = (
  links: ProductionBatch['links'],
  ncFileStatus: ProductionBatch['ncFileStatus'],
): string => {
  if (links?.length) return 'Linked to';
  if (ncFileStatus === ncFileStatuses.Unknown) return 'Batch of unknown NC File';
  return 'Batch not linked to Job';
};

export const getDirectionConfig = (direction: 'up' | 'down'): { color: string; rotate: string } => {
  switch (direction) {
    case 'up':
      return { color: 'Label/Light/Tertiary', rotate: '90deg' };
    case 'down':
      return { color: 'Label/Light/Warning', rotate: '-90deg' };
  }
};

export const isBatchLinked = (links: ProductionBatch['links']): boolean => {
  return (links?.length ?? 0) > 0;
};

export const getQuantityProduced = (batch: { links?: ProductionBatch['links']; quantity?: number | null; quantityReady?: number | null }): number | null | undefined => {
  return isBatchLinked(batch?.links) ? batch?.quantityReady : batch?.quantity;
};

export const getTimeDisplayValues = (time: Record<string, string | number>): {
  firstValue: number;
  secondValue: number;
  firstFormat: string;
  secondFormat: string;
} => {
  const isHours = Number(time.h) > 0;
  return {
    firstValue: Number(isHours ? time.h : time.m),
    secondValue: Number(isHours ? time.m : time.s),
    firstFormat: isHours ? 'h' : 'm',
    secondFormat: isHours ? 'm' : 's',
  };
};

export const getTimeValues = (
  batch: { isInProgress?: boolean; actualTotalTimeTotalInSeconds?: number | null; idealTotalTimeTotalInSeconds?: number | null; scheduledTotalTimeTotalInSeconds?: number | null },
  batchStatistics: { actualTotalTimeTotalInSeconds?: number | null; idealTotalTimeTotalInSeconds?: number | null; paidTotalTimeTotalInSeconds?: number | null } | null,
): { actualTime: number | null | undefined; idealTime: number | null | undefined; paidTime: number | null | undefined; scheduledTime: number | null | undefined } => {
  const actualTime = !batch?.isInProgress ? batch?.actualTotalTimeTotalInSeconds : batchStatistics?.actualTotalTimeTotalInSeconds;
  const idealTime = !batch?.isInProgress ? batch?.idealTotalTimeTotalInSeconds : batchStatistics?.idealTotalTimeTotalInSeconds;
  const paidTime = batchStatistics?.paidTotalTimeTotalInSeconds;
  const scheduledTime = batch?.scheduledTotalTimeTotalInSeconds;
  return { actualTime, idealTime, paidTime, scheduledTime };
};

type TimeDifferenceConfig = { lessThanTarget: boolean; timeDifferenceInPercentage: string | number };

export const buildTimeDifferenceConfigs = (
  timeValues: { actualTime: number | null | undefined; idealTime: number | null | undefined; paidTime: number | null | undefined; scheduledTime: number | null | undefined },
  calculatePercentage: (target: number | null | undefined, actual: number | null | undefined) => string | number | null,
): { ideal?: TimeDifferenceConfig; scheduled?: TimeDifferenceConfig; paid?: TimeDifferenceConfig } => {
  const { actualTime, idealTime, paidTime, scheduledTime } = timeValues;
  const idealPct = calculatePercentage(idealTime, actualTime);
  const scheduledPct = calculatePercentage(scheduledTime, actualTime);
  const paidPct = calculatePercentage(paidTime, actualTime);

  return {
    ...(idealPct ? { ideal: { lessThanTarget: idealTime != null && actualTime != null && idealTime < actualTime, timeDifferenceInPercentage: idealPct } } : {}),
    ...(scheduledPct ? { scheduled: { lessThanTarget: scheduledTime != null && actualTime != null && scheduledTime < actualTime, timeDifferenceInPercentage: scheduledPct } } : {}),
    ...(paidPct ? { paid: { lessThanTarget: paidTime != null && actualTime != null && paidTime < actualTime, timeDifferenceInPercentage: paidPct } } : {}),
  };
};

export const resolveTileComponent = (elementSizeConfig: { small: boolean; middle: boolean; large: boolean; ultraLarge: boolean }): string => {
  return (Object.entries(elementSizeConfig).find(([, value]) => value)?.[0] || 'small');
};

export const shouldLoadBatchStatistics = (batch: { isInProgress?: boolean; links?: ProductionBatch['links'] }): boolean => {
  return !!(batch.isInProgress || (batch.links?.length ?? 0) > 0);
};
